import React from "react";
import { Link } from "react-router-dom";
import {
  MdArrowBack,
  MdChat,
  MdImage,
  MdAttachFile,
  MdInsertDriveFile,
  MdMic,
  MdPlayArrow,
  MdSend,
  MdStop,
  MdNotifications,
  MdNotificationsOff,
  MdVideocam,
} from "react-icons/md";
import { useAppLanguage } from "hooks/useAppLanguage";
import { useFirebaseDirectChat } from "hooks/useFirebaseDirectChat";
import { directChatService } from "services/directChatService";
import type { CourseChatMessage } from "services/courseChatService";
import {
  fileExtensionLabel,
  formatFileSize,
  isVideoAttachment,
  mergeChatMessages,
} from "utils/chatAttachment";
import EmptyState from "components/ui/EmptyState";
import { Skeleton } from "components/ui/Skeleton";
import { ChatVideoPreviewModal } from "components/chat/ChatVideoPreviewModal";
import { toastError } from "lib/toast";

function makeClientId(): string {
  return `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function formatTime(iso: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

const DM_URL_SOFT_TTL_MS = 5 * 60 * 1000;
const dmUrlCache = new Map<string, { url: string; expiresAt: number }>();
const dmUrlInflight = new Map<string, Promise<string>>();

async function getDirectAttachmentUrl(
  conversationId: number | string,
  messageId: string,
  forceRefresh = false
): Promise<string> {
  const key = `dm:${conversationId}:${messageId}`;
  if (!forceRefresh) {
    const cached = dmUrlCache.get(key);
    if (cached && cached.expiresAt > Date.now()) return cached.url;
    const pending = dmUrlInflight.get(key);
    if (pending) return pending;
  } else {
    dmUrlCache.delete(key);
  }

  const promise = directChatService
    .resolveAttachmentUrl(conversationId, messageId)
    .then((url) => {
      dmUrlCache.set(key, { url, expiresAt: Date.now() + DM_URL_SOFT_TTL_MS });
      dmUrlInflight.delete(key);
      return url;
    })
    .catch((err) => {
      dmUrlInflight.delete(key);
      throw err;
    });

  dmUrlInflight.set(key, promise);
  return promise;
}

function DirectMessageBubble({
  message,
  conversationId,
  isArabic,
}: {
  message: CourseChatMessage;
  conversationId: number | string;
  isArabic: boolean;
}) {
  const mine = message.is_mine;
  const isLocal = message.local === true;
  const isVideo = isVideoAttachment(message);
  const [attachmentUrl, setAttachmentUrl] = React.useState<string | null>(
    message.attachment?.url ?? message.local_preview_url ?? null
  );
  const [videoOpen, setVideoOpen] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    if (isLocal) {
      setAttachmentUrl(message.attachment?.url ?? message.local_preview_url ?? null);
      return;
    }
    const needsUrl =
      message.attachment &&
      !message.attachment.url &&
      (message.attachment.path || message.message_type !== "text");

    if (!needsUrl) {
      setAttachmentUrl(message.attachment?.url ?? null);
      return;
    }

    void getDirectAttachmentUrl(conversationId, message.id)
      .then((url) => {
        if (!cancelled) setAttachmentUrl(url);
      })
      .catch(() => {
        if (!cancelled) setAttachmentUrl(null);
      });

    return () => {
      cancelled = true;
    };
  }, [
    conversationId,
    isLocal,
    message.attachment,
    message.id,
    message.local_preview_url,
    message.message_type,
  ]);

  const openVideo = async () => {
    if (isLocal) return;
    try {
      const url = attachmentUrl ?? (await getDirectAttachmentUrl(conversationId, message.id));
      setAttachmentUrl(url);
      setVideoOpen(true);
    } catch {
      try {
        const url = await getDirectAttachmentUrl(conversationId, message.id, true);
        setAttachmentUrl(url);
        setVideoOpen(true);
      } catch {
        toastError(isArabic ? "تعذّر فتح الفيديو" : "Failed to open video");
      }
    }
  };

  const openOrDownload = async () => {
    if (isLocal) return;
    try {
      const url = attachmentUrl ?? (await getDirectAttachmentUrl(conversationId, message.id));
      window.open(url, "_blank", "noreferrer");
    } catch {
      try {
        const url = await getDirectAttachmentUrl(conversationId, message.id, true);
        window.open(url, "_blank", "noreferrer");
      } catch {
        toastError(isArabic ? "تعذّر فتح الملف" : "Failed to open file");
      }
    }
  };

  return (
    <>
      <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
        <div
          className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
            mine
              ? "rounded-br-md bg-brand-500 text-white"
              : "rounded-bl-md bg-white text-gray-800 dark:bg-navy-800 dark:text-white"
          }`}
        >
          {message.message_type === "text" ? (
            <p className="whitespace-pre-wrap break-words">{message.body}</p>
          ) : null}

          {message.message_type === "image" && attachmentUrl ? (
            isLocal ? (
              <img src={attachmentUrl} alt="" className="max-h-56 rounded-lg object-cover" />
            ) : (
              <a href={attachmentUrl} target="_blank" rel="noreferrer">
                <img src={attachmentUrl} alt="" className="max-h-56 rounded-lg object-cover" />
              </a>
            )
          ) : null}

          {message.message_type === "image" && !attachmentUrl ? (
            <span>{isArabic ? "📷 صورة" : "📷 Image"}</span>
          ) : null}

          {message.message_type === "file" && message.attachment ? (
            isVideo ? (
              <div className="flex w-60 max-w-full items-center gap-3 rounded-lg bg-black/5 p-2 dark:bg-white/5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-500/15">
                  <MdVideocam className="h-6 w-6 text-brand-600 dark:text-brand-300" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold">
                    {message.attachment.name ?? (isArabic ? "فيديو" : "Video")}
                  </p>
                  <p className="text-[10px] opacity-70">
                    {fileExtensionLabel(message)}
                    {message.attachment.size ? ` · ${formatFileSize(message.attachment.size)}` : ""}
                  </p>
                  {!isLocal ? (
                    <button
                      type="button"
                      onClick={() => void openVideo()}
                      className="mt-1 text-[11px] font-semibold underline"
                    >
                      {isArabic ? "تشغيل" : "Play"}
                    </button>
                  ) : null}
                </div>
                {!isLocal ? (
                  <button
                    type="button"
                    onClick={() => void openVideo()}
                    className="shrink-0 rounded-lg p-1.5 hover:bg-black/10 dark:hover:bg-white/10"
                    title={isArabic ? "تشغيل" : "Play"}
                  >
                    <MdPlayArrow className="h-5 w-5" />
                  </button>
                ) : null}
              </div>
            ) : (
              <button
                type="button"
                disabled={isLocal}
                onClick={() => void openOrDownload()}
                className="flex w-60 max-w-full items-center gap-3 rounded-lg bg-black/5 p-2 text-start dark:bg-white/5 disabled:opacity-60"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-black/10 dark:bg-white/10">
                  <MdInsertDriveFile className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold">
                    {message.attachment.name ?? (isArabic ? "ملف" : "File")}
                  </p>
                  <p className="text-[10px] opacity-70">
                    {fileExtensionLabel(message)}
                    {message.attachment.size ? ` · ${formatFileSize(message.attachment.size)}` : ""}
                  </p>
                </div>
              </button>
            )
          ) : null}

          {message.message_type === "voice" ? (
            attachmentUrl && !isLocal ? (
              <audio controls src={attachmentUrl} className="max-w-full" />
            ) : (
              <span>{isArabic ? "🎤 رسالة صوتية" : "🎤 Voice message"}</span>
            )
          ) : null}

          <div className={`mt-1 text-[10px] ${mine ? "text-white/80" : "text-gray-400"}`}>
            {formatTime(message.created_at)}
            {message.status === "failed" ? (isArabic ? " · فشل" : " · Failed") : ""}
          </div>
        </div>
      </div>

      {videoOpen && attachmentUrl ? (
        <ChatVideoPreviewModal
          url={attachmentUrl}
          name={message.attachment?.name ?? null}
          mimeType={message.attachment?.mime}
          isArabic={isArabic}
          onClose={() => setVideoOpen(false)}
        />
      ) : null}
    </>
  );
}

type Props = {
  conversationId: number | string;
  peerName?: string | null;
};

export default function DirectChatPanel({ conversationId, peerName }: Props) {
  const language = useAppLanguage();
  const isArabic = language === "ar";

  const [realMessages, setRealMessages] = React.useState<CourseChatMessage[]>([]);
  const [pending, setPending] = React.useState<CourseChatMessage[]>([]);
  const [peerTitle, setPeerTitle] = React.useState(peerName ?? "");
  const [canSend, setCanSend] = React.useState(true);
  const [notificationsMuted, setNotificationsMuted] = React.useState(false);
  const [notificationsMuteBusy, setNotificationsMuteBusy] = React.useState(false);
  const [text, setText] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const imageInputRef = React.useRef<HTMLInputElement | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const mediaRecorderRef = React.useRef<MediaRecorder | null>(null);
  const recordChunksRef = React.useRef<Blob[]>([]);
  const [recording, setRecording] = React.useState(false);

  const messages = React.useMemo(() => mergeChatMessages(realMessages, pending), [realMessages, pending]);

  const { connectionMode } = useFirebaseDirectChat(conversationId, {
    onMessages: (next) => {
      setRealMessages(next);
      setPending((prev) => {
        if (prev.length === 0) return prev;
        const ids = new Set(next.map((m) => m.client_message_id).filter(Boolean));
        return prev.filter((p) => p.status === "failed" || !p.client_message_id || !ids.has(p.client_message_id));
      });
    },
    onBootstrap: (bootstrap) => {
      setCanSend(bootstrap.permissions?.can_send !== false);
      setNotificationsMuted(bootstrap.notifications_muted === true);
      if (bootstrap.peer?.name) setPeerTitle(bootstrap.peer.name);
    },
  });

  React.useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  React.useEffect(() => {
    let interval: number | null = null;

    const clearBeat = () => {
      if (interval != null) {
        window.clearInterval(interval);
        interval = null;
      }
    };

    const beat = () => {
      void directChatService.heartbeat(conversationId, true).catch(() => undefined);
    };

    const leave = () => {
      clearBeat();
      void directChatService.leaveChat(conversationId).catch(() => undefined);
    };

    const start = () => {
      if (document.visibilityState !== "visible") return;
      clearBeat();
      beat();
      interval = window.setInterval(beat, 45_000);
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        start();
      } else {
        leave();
      }
    };

    start();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", leave);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", leave);
      leave();
    };
  }, [conversationId]);

  const toggleNotificationsMute = async () => {
    if (notificationsMuteBusy) return;
    const next = !notificationsMuted;
    setNotificationsMuteBusy(true);
    setNotificationsMuted(next);
    try {
      const result = await directChatService.setNotificationsMuted(conversationId, next);
      setNotificationsMuted(result.notifications_muted === true);
    } catch {
      setNotificationsMuted(!next);
      toastError(isArabic ? "تعذّر تحديث كتم الإشعارات" : "Failed to update notification mute");
    } finally {
      setNotificationsMuteBusy(false);
    }
  };

  const buildOptimistic = (
    partial: Pick<CourseChatMessage, "message_type" | "body" | "attachment"> & {
      clientId: string;
      status: CourseChatMessage["status"];
      localPreview?: string | null;
    }
  ): CourseChatMessage => {
    const now = Date.now();
    return {
      id: partial.clientId,
      course_id: Number(conversationId),
      sender: { id: 0, name: null, role: "instructor", avatar_url: null },
      message_type: partial.message_type,
      body: partial.body,
      attachment: partial.attachment,
      client_message_id: partial.clientId,
      is_mine: true,
      read_count: null,
      created_at: new Date(now).toISOString(),
      created_at_millis: now,
      created_at_human: null,
      is_deleted: false,
      status: partial.status,
      local: true,
      local_created_at_millis: now,
      local_preview_url: partial.localPreview ?? null,
    };
  };

  const sendText = async () => {
    const body = text.trim();
    if (!body || !canSend || sending) return;
    const clientId = makeClientId();
    setPending((prev) => [
      ...prev,
      buildOptimistic({ clientId, status: "sending", message_type: "text", body, attachment: null }),
    ]);
    setText("");
    setSending(true);
    try {
      await directChatService.sendText(conversationId, body, clientId);
      setPending((prev) =>
        prev.map((m) => (m.client_message_id === clientId ? { ...m, status: "sent" } : m))
      );
    } catch {
      setPending((prev) =>
        prev.map((m) => (m.client_message_id === clientId ? { ...m, status: "failed" } : m))
      );
    } finally {
      setSending(false);
    }
  };

  const sendFile = async (file: File, messageType: "image" | "file") => {
    if (!canSend || sending) return;
    const clientId = makeClientId();
    const preview = messageType === "image" ? URL.createObjectURL(file) : null;
    setPending((prev) => [
      ...prev,
      buildOptimistic({
        clientId,
        status: "uploading",
        message_type: messageType,
        body: null,
        attachment: {
          url: null,
          name: file.name,
          mime: file.type,
          size: file.size,
          duration_seconds: null,
        },
        localPreview: preview,
      }),
    ]);
    setSending(true);
    try {
      await directChatService.sendAttachment(conversationId, messageType, file, {
        clientMessageId: clientId,
      });
      setPending((prev) =>
        prev.map((m) => (m.client_message_id === clientId ? { ...m, status: "sent" } : m))
      );
    } catch {
      setPending((prev) =>
        prev.map((m) => (m.client_message_id === clientId ? { ...m, status: "failed" } : m))
      );
    } finally {
      setSending(false);
    }
  };

  const startRecording = async () => {
    if (!canSend || recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      recordChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(recordChunksRef.current, { type: "audio/webm" });
        const file = new File([blob], "voice.webm", { type: "audio/webm" });
        void (async () => {
          const clientId = makeClientId();
          setPending((prev) => [
            ...prev,
            buildOptimistic({
              clientId,
              status: "uploading",
              message_type: "voice",
              body: null,
              attachment: {
                url: null,
                name: "voice.webm",
                mime: "audio/webm",
                size: file.size,
                duration_seconds: null,
              },
            }),
          ]);
          try {
            await directChatService.sendAttachment(conversationId, "voice", file, {
              clientMessageId: clientId,
            });
            setPending((prev) =>
              prev.map((m) => (m.client_message_id === clientId ? { ...m, status: "sent" } : m))
            );
          } catch {
            setPending((prev) =>
              prev.map((m) => (m.client_message_id === clientId ? { ...m, status: "failed" } : m))
            );
          }
        })();
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch {
      // ignore mic errors
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    mediaRecorderRef.current = null;
    setRecording(false);
  };

  const connectionLabel =
    connectionMode === "live"
      ? isArabic
        ? "مباشر"
        : "Live"
      : connectionMode === "connecting"
        ? isArabic
          ? "جاري الاتصال"
          : "Connecting"
        : isArabic
          ? "تعذّر الاتصال"
          : "Offline";

  return (
    <div className="flex h-[calc(100vh-7rem)] min-h-[420px] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-navy-800">
      <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3 dark:border-white/10">
        <Link
          to="/instructor/messages"
          className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-white/10 dark:text-white"
        >
          <MdArrowBack className="h-5 w-5" />
        </Link>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-base font-bold text-navy-700 dark:text-white">
            {peerTitle || (isArabic ? "محادثة" : "Conversation")}
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {connectionLabel}
            {notificationsMuted
              ? isArabic
                ? " · الإشعارات مكتومة"
                : " · Notifications muted"
              : ""}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void toggleNotificationsMute()}
          disabled={notificationsMuteBusy || connectionMode === "error"}
          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1 text-xs font-semibold disabled:opacity-50 ${
            notificationsMuted
              ? "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-200"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/10 dark:text-white"
          }`}
        >
          {notificationsMuted ? <MdNotificationsOff size={14} /> : <MdNotifications size={14} />}
          {notificationsMuted
            ? isArabic
              ? "إلغاء كتم الإشعارات"
              : "Unmute notifications"
            : isArabic
              ? "كتم الإشعارات"
              : "Mute notifications"}
        </button>
      </div>

      <div className="course-chat-wallpaper relative min-h-0 flex-1">
        <div
          ref={listRef}
          className="absolute inset-0 space-y-2 overflow-y-auto px-4 py-4"
        >
        {connectionMode === "connecting" && messages.length === 0 ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-2/3 rounded-2xl" />
            <Skeleton className="ms-auto h-12 w-1/2 rounded-2xl" />
          </div>
        ) : null}

        {connectionMode === "error" && messages.length === 0 ? (
          <EmptyState
            icon={<MdChat className="h-6 w-6" />}
            title={isArabic ? "تعذّر تحميل المحادثة" : "Could not load conversation"}
            description={
              isArabic
                ? "تحقق من الاتصال ثم أعد فتح الصفحة."
                : "Check your connection and reopen the page."
            }
          />
        ) : null}

        {connectionMode === "live" && messages.length === 0 ? (
          <EmptyState
            icon={<MdChat className="h-6 w-6" />}
            title={isArabic ? "لا توجد رسائل بعد" : "No messages yet"}
            description={
              isArabic
                ? "ابدأ محادثة خاصة مع الطالب."
                : "Start a private conversation with the student."
            }
          />
        ) : null}

        {messages.map((message) => (
          <DirectMessageBubble
            key={message.id}
            message={message}
            conversationId={conversationId}
            isArabic={isArabic}
          />
        ))}
        </div>
      </div>

      <div className="border-t border-gray-200 p-3 dark:border-white/10">
        {!canSend ? (
          <p className="mb-2 text-center text-xs text-amber-600 dark:text-amber-300">
            {isArabic
              ? "لا يمكن الإرسال — تحقق من أن الطالب ما زال مسجّلاً في كورس فعّال."
              : "Sending disabled — student needs an active enrollment in your course."}
          </p>
        ) : null}
        <div className="flex items-end gap-2">
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) void sendFile(file, "image");
            }}
          />
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) void sendFile(file, "file");
            }}
          />
          <button
            type="button"
            disabled={!canSend}
            onClick={() => imageInputRef.current?.click()}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-600 disabled:opacity-40 dark:bg-white/10 dark:text-white"
          >
            <MdImage className="h-5 w-5" />
          </button>
          <button
            type="button"
            disabled={!canSend}
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-600 disabled:opacity-40 dark:bg-white/10 dark:text-white"
          >
            <MdAttachFile className="h-5 w-5" />
          </button>
          <button
            type="button"
            disabled={!canSend}
            onClick={() => (recording ? stopRecording() : void startRecording())}
            className={`inline-flex h-10 w-10 items-center justify-center rounded-full disabled:opacity-40 ${
              recording
                ? "bg-red-500 text-white"
                : "bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-white"
            }`}
          >
            {recording ? <MdStop className="h-5 w-5" /> : <MdMic className="h-5 w-5" />}
          </button>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void sendText();
              }
            }}
            rows={1}
            disabled={!canSend}
            placeholder={isArabic ? "اكتب رسالة..." : "Type a message..."}
            className="min-h-[40px] flex-1 resize-none rounded-2xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
          />
          <button
            type="button"
            disabled={!canSend || !text.trim() || sending}
            onClick={() => void sendText()}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-500 text-white disabled:opacity-40"
          >
            <MdSend className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
